import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';
import type { Store } from '@warp-drive/core';
import type { Employee } from '../data/resources/employee.type.ts';
import { reloadRecord } from './save-record.ts';

/**
 * Employee action request builders.
 *
 * Replaces the EMPLOYEE-shared custom methods on the near-identical
 * `tio-employee/app/adapters/employee.ts` and `tio-admin/app/adapters/employee.ts`.
 *
 * URL resource path: ALL endpoints are PLURAL `/employees/{id}/...`. Both
 * legacy adapter forms resolve there — `this.buildURL('employees', ...)` is
 * plural directly, and `this.buildURL('employee', id)` (used by the accounting
 * GETs + add-employee-dependent) is ALSO pluralized to `/employees/{id}` by
 * Ember Data's default inflector (EmployeeAdapter has no pathForType override).
 * A single `employeesUrl` helper covers them all. (A prior revision wrongly
 * split out a "singular" `/employee/{id}` helper; the flow-diff against main —
 * which sends `/employees/{id}/accounting_payments` — confirmed plural.)
 *
 * demoMode (employee seam B): the legacy employee adapter consulted
 * `demoMode.demoJsonFor(url, 'GET')` BEFORE the two payment GETs
 * (`accounting_payments` / `accounting_preview`) because its raw `this.ajax`
 * bypassed the request-manager demo handler. Routing the call sites through
 * `store.request(...)` honours the demo seam natively (the terminal `Fetch`
 * handler's `window.fetch` is intercepted by `DemoModeService`), so a
 * byte-identical URL preserves demo behaviour with NO explicit guard.
 *
 * BODY CASING — preserved EXACTLY from the legacy adapters:
 * - reinstate / terminate / update-overrides send a JSON:API-ish
 *   `{ data: { attributes: { ... } } }` envelope (Ember Data's `data` option IS
 *   the request body — see below).
 * - `self-register` sends a flat object whose keys are whatever the call site
 *   passes (today `{ email, first_name, last_name }`).
 * - `add-employee-dependent` sends a flat snake_case object
 *   `{ first_name, last_name, email, birth_date, relationship }`.
 *
 * `_changelog` is the transient, write-only attribute the API records on each
 * mutation; it is parameterized everywhere the legacy adapter sent it.
 *
 * ADMIN methods (wave 3.2c): `getBackpayOptions` (GET
 * /employees/:id/accounting_backpays), `createInvite` (PATCH
 * /employees/:id/create_invite), `sendInviteEmail` (PATCH /employees/:id/invite),
 * and `clearEmployeeHash` (PATCH /employees/:id/clear-employee-hash) are
 * single-app (admin) surface, now built here for the 3.2c-ii call-site
 * conversion. `createInviteRequest` / `sendInviteEmailRequest` (below) are
 * already converted at `templates/authenticated/people/show.gts`;
 * `getBackpayOptionsRequest` (a named alias of {@link accountingBackpaysRequest},
 * matching the adapter method name the create-dialog call site uses) and
 * {@link clearEmployeeHashRequest} are added for their remaining admin call sites
 * (`accounting-invoices/.../create-dialog.gts`, `employee/summary.gts`).
 *
 * The `grantGenericRelationship` / `grantBulkGenericRelationship` /
 * `getGenericRelationships` admin methods live in
 * `requests/generic-relationship.ts`, not here.
 */

const employeesUrlOptions: QueryUrlOptions = {
  identifier: { type: 'employees' },
  op: 'query',
  resourcePath: 'employees',
};

/**
 * PLURAL `/employees/{id}/...` for EVERY employee action endpoint, including
 * the accounting GETs + add-employee-dependent. The legacy adapter built those
 * via `this.buildURL('employee', id)`, which ALSO resolves to PLURAL
 * `/employees/{id}` — Ember Data pluralizes the type via the default inflector
 * (EmployeeAdapter has no pathForType override). An earlier split into a
 * "singular" helper was a misread of buildURL; the flow-diff against main
 * confirmed the wire is plural.
 */
function employeesUrl(path?: string): string {
  const base = buildBaseURL(employeesUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/**
 * GET /employees/:id/accounting_payments
 *
 * Mirrors `EmployeeAdapter#getAccountingPaymentContributions`, which built the
 * URL via `this.buildURL('employee', employeeId) + '/accounting_payments'` and
 * sent `this.ajax(url, 'GET')`. The employee adapter consulted `demoMode`
 * first; that seam is honoured natively once routed through `store.request`.
 * The response is raw JSON (`{ payments, summary }`); the call site reads it off
 * `content`.
 */
export function accountingPaymentsRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_payments`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /employees/:id/accounting_preview
 *
 * Mirrors `EmployeeAdapter#getAccountingFuturePayments`, which built the URL via
 * `this.buildURL('employee', employeeId) + '/accounting_preview'` and sent
 * `this.ajax(url, 'GET')`. The employee adapter consulted `demoMode` first; that
 * seam is honoured natively once routed through `store.request`. The response is
 * raw JSON (an array of preview payments); the call site reads it off `content`.
 */
export function accountingPreviewRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_preview`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
    // Non-critical dashboard fetch whose failures must not surface globally —
    // see SkipErrorReporting docs. All call sites catch locally.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}

/**
 * GET /employees/:id/accounting_annual_preview
 *
 * Mirrors `EmployeeAdapter#getAnnualPreview`, which built the URL via
 * `this.buildURL('employee', employeeId) + '/accounting_annual_preview'` and
 * sent `this.ajax(url, 'GET')` (no demoMode guard). Raw JSON response; the call
 * site reads it off `content`.
 */
export function accountingAnnualPreviewRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_annual_preview`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /employees/:id/accounting_backpays
 *
 * Mirrors `EmployeeAdapter#getBackpayOptions` (admin adapter only), which built
 * the URL via `this.buildURL('employee', employeeId) + '/accounting_backpays'`
 * and sent `this.ajax(url, 'GET')`. Provided for parity completeness of the
 * accounting-GET family; its sole call site is admin-only and converts in
 * wave 3 (see module TODO). Raw JSON response; read off `content`.
 */
export function accountingBackpaysRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_backpays`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /employees/:id/accounting_backpays — named alias of
 * {@link accountingBackpaysRequest} matching the legacy admin adapter method
 * name (`EmployeeAdapter#getBackpayOptions`). Used by the admin call site
 * (`accounting-invoices/line-items/create-dialog.gts`). Identical wire — no separate request shape.
 */
export const getBackpayOptionsRequest = accountingBackpaysRequest;

/** The transient, write-only changelog string the API records on a mutation. */
export type EmployeeChangelog = string;

/**
 * PATCH /employees/:id/reinstate
 * body `{ data: { attributes: { _changelog } } }`
 *
 * Mirrors `EmployeeAdapter#reinstateEmployee`, which built the URL via
 * `this.buildURL('employees', model.id) + '/reinstate'` and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { _changelog: changelog } } } })`
 * (Ember Data's `data` option IS the request body, so the wire body is the inner
 * `{ data: { attributes: { _changelog } } }`). The legacy method then awaited
 * `model.reload()`; the call site now reloads explicitly after the request.
 */
export function reinstateEmployeeRequest(id: string, changelog?: EmployeeChangelog): RequestInfo {
  return {
    url: employeesUrl(`${id}/reinstate`),
    method: 'PATCH',
    body: JSON.stringify({ data: { attributes: { _changelog: changelog } } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * DELETE /employees/:id
 * body `{ data: { attributes: { _changelog } } }`
 *
 * Mirrors the EMPLOYEE shape of `EmployeeAdapter#terminateEmployee`, which built
 * the URL via `this.buildURL('employees', model.id)` and sent
 * `this.ajax(url, 'DELETE', { data: { data: { attributes: { _changelog: changelog } } } })`.
 *
 * The ADMIN adapter variant additionally sends `syf_grace_acknowledged`
 * alongside `_changelog`. That field is exposed here as an OPTIONAL parameter
 * (`syfGraceAcknowledged`) so the admin wave-3 call sites can reuse this builder
 * byte-identically; it is OMITTED from the wire body entirely when undefined, so
 * the employee call sites stay byte-identical to the employee adapter. The
 * legacy method then awaited `model.reload()`; the call site now reloads
 * explicitly after the request.
 */
export function terminateEmployeeRequest(
  id: string,
  changelog?: EmployeeChangelog,
  syfGraceAcknowledged?: boolean
): RequestInfo {
  const attributes: { _changelog?: EmployeeChangelog; syf_grace_acknowledged?: boolean } = {
    _changelog: changelog,
  };
  if (syfGraceAcknowledged !== undefined) {
    attributes.syf_grace_acknowledged = syfGraceAcknowledged;
  }
  return {
    url: employeesUrl(id),
    method: 'DELETE',
    body: JSON.stringify({ data: { attributes } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/update-overrides
 * body `{ data: { attributes: { overrides: [override], _changelog } } }`
 *
 * Mirrors `EmployeeAdapter#updateOverrides`, which built the URL via
 * `this.buildURL('employees', employeeId) + '/update-overrides'` and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { overrides: [overrides], _changelog: changelog } } } })`
 * — note the SINGLE override object is wrapped in a one-element array, preserved
 * exactly here.
 */
export function updateOverridesRequest(
  id: string,
  override: Record<string, unknown>,
  changelog: EmployeeChangelog
): RequestInfo {
  return {
    url: employeesUrl(`${id}/update-overrides`),
    method: 'PATCH',
    body: JSON.stringify({
      data: { attributes: { overrides: [override], _changelog: changelog } },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /employees/self-register  (PLURAL, no id)
 *
 * Mirrors `EmployeeAdapter#selfRegister`, which built the URL via
 * `this.buildURL('employees') + '/self-register'` and sent
 * `this.ajax(url, 'POST', { data })` — the `data` object is sent verbatim as the
 * request body. The body keys are exactly whatever the call site passes; the
 * sole call site sends `{ email, first_name, last_name }`. The body shape is
 * therefore parameterized (not hard-coded) so the wire stays byte-identical to
 * whatever the caller hands in.
 */
export function selfRegisterRequest(data: Record<string, unknown>): RequestInfo {
  return {
    url: employeesUrl('self-register'),
    method: 'POST',
    body: JSON.stringify(data),
    cacheOptions: { [SkipCache]: true },
  };
}

/** The flat snake_case body for `add-employee-dependent`. */
export interface AddEmployeeDependentBody {
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string;
  relationship: string;
}

/**
 * POST /employee/:id/add-employee-dependent
 * body `{ first_name, last_name, email, birth_date, relationship }` (snake_case)
 *
 * Mirrors `EmployeeAdapter#addEmployeeDependent`, which built the URL via
 * `this.buildURL('employee', employeeId) + '/add-employee-dependent'`
 * and sent
 * `this.ajax(url, 'POST', { data: { first_name, last_name, email, birth_date, relationship } })`.
 * The snake_case key casing is preserved EXACTLY.
 */
export function addEmployeeDependentRequest(
  employeeId: string,
  data: AddEmployeeDependentBody
): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/add-employee-dependent`),
    method: 'POST',
    body: JSON.stringify({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      birth_date: data.birth_date,
      relationship: data.relationship,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/create_invite  (admin-only surface)
 *
 * Mirrors `EmployeeAdapter#createInvite` (tio-admin adapter), which built the URL
 * via `this.buildURL('employees', employeeId) + '/create_invite'` and sent
 * `this.ajax(url, 'PATCH')` with NO body. No request body is emitted here either.
 */
export function createInviteRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/create_invite`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/invite  (admin-only surface)
 *
 * Mirrors `EmployeeAdapter#sendInviteEmail` (tio-admin adapter), which built the
 * URL via `this.buildURL('employees', employeeId) + '/invite'` and sent
 * `this.ajax(url, 'PATCH')` with NO body. No request body is emitted here either.
 */
export function sendInviteEmailRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/invite`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/clear-employee-hash  (admin-only surface, NO body)
 *
 * Mirrors `EmployeeAdapter#clearEmployeeHash` (tio-admin adapter), which built
 * the URL via `this.buildURL('employees', employeeId) + '/clear-employee-hash'`
 * and sent `this.ajax(url, 'PATCH')` with NO body. No request body is emitted
 * here either.
 *
 * NOTE (route): `clear-employee-hash` is NOT present in the tio-api
 * `config/routes.rb` snapshot in this workspace (the `:employees` member block at
 * config/routes.rb:296-310 wires reinstate/invite/create_invite/etc but not this
 * one). The wire is taken from the LEGACY ADAPTER (authoritative for what the FE
 * emits; WarpDrive parity is byte-identical-to-adapter). Flagged for the API-side
 * owner to confirm the route exists.
 */
export function clearEmployeeHashRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/clear-employee-hash`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Terminate an employee and reload it.
 *
 * Replaces the legacy `employee.terminateEmployee()` method on
 * `EmployeeExtension` — WarpDrive resource extensions can't participate in
 * Ember DI (no owner on schema-record proxies), so request-issuing helpers
 * live here as free functions and take `store` explicitly, matching
 * `saveRecord` / `reloadRecord` in `save-record.ts`.
 *
 * PLURAL DELETE /employees/{id}; the JSONAPI::Resources destroy action returns
 * `head 204` (no resource body), so the cache is NOT refreshed — keep the
 * reload via the builder helper.
 */
export async function terminateEmployee(store: Store, employee: Employee): Promise<Employee> {
  await store.request(terminateEmployeeRequest(employee.id as string));
  return reloadRecord(store, employee);
}

/**
 * Reinstate an employee and reload it.
 *
 * Replaces the legacy `employee.reinstateEmployee()` method on
 * `EmployeeExtension` — WarpDrive resource extensions can't participate in
 * Ember DI (no owner on schema-record proxies), so request-issuing helpers
 * live here as free functions and take `store` explicitly, matching
 * `saveRecord` / `reloadRecord` in `save-record.ts`.
 *
 * PLURAL PATCH /employees/{id}/reinstate; the controller's perform_action
 * returns `head 204` (api_controller.rb), so the cache is NOT refreshed — keep
 * the reload via the builder helper.
 */
export async function reinstateEmployee(store: Store, employee: Employee): Promise<Employee> {
  await store.request(reinstateEmployeeRequest(employee.id as string));
  return reloadRecord(store, employee);
}
